/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
// libraries
import React from 'react';
import {
    string, shape, object, bool,
} from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import { useDispatch } from 'react-redux';
import useMediaQuery from '@material-ui/core/useMediaQuery';

// components
import { Container, makeStyles } from '@material-ui/core';
import findCategoryPageByUrl from '../../../../gql/queries/findCategoryPageByURL';
import CustomCarousel from './Partials/CustomCarousel';
import { trackEvent } from '../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

const useStyles = makeStyles((theme) => ({
    ' .plants-featured-container': {
        marginBottom: '0.5rem',
        minHeight: '400px',
    },
    plantsFeatured: {
        '&.featuredPlants': {
            marginBottom: '50px !important',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            [theme.breakpoints.down('xs')]: {
                padding: '0',
                marginTop: '0',
                marginBottom: '10px !important',
            },
            '& a': {
                textDecoration: 'none',
            },
            '& .productTitle, & .pricelist': {
                float: 'none !important',
                width: '100% !important',
                display: 'block !important',
                textAlign: 'left !important',
            },
            '& .material-icons': {
                fontSize: '2.625rem',
            },
            '& .slick-center': {
                '-webkit-transform': 'scale(1.39)',
                '-moz-transform': 'scale(1.39)',
                transform: 'scale(1.39)',
                [theme.breakpoints.down('xs')]: {
                    marginTop: '18px',
                    paddingLeft: '0px !important',
                },
                '& .title.bgsTitle.productTitle, & .pricelist': {
                    fontSize: '0.875rem !important',
                    wordBreak: 'break-word',
                    [theme.breakpoints.down('sm')]: {
                        fontSize: '0.750rem',
                    },
                },
                '& svg': {
                    width: '22px !important',
                    height: '22px !important',
                    display: 'block',
                },
            },
            '& .slick-list': {
                padding: '55px 0px 30px !important',
                [theme.breakpoints.down('sm')]: {
                    padding: '30px 0px !important',
                    '& .slick-slide': {
                        paddingRight: '10px',
                    },
                },
            },
            '& .slick-next': {
                right: 5,
                top: 'calc(50% - 42px)',
                [theme.breakpoints.down('sm')]: {
                    right: 16,
                },
            },
            '& .slick-prev': {
                top: 'calc(50% - 42px)',
            },
            '& .featuredTitle': {
                position: 'relative',
                display: 'flex',
                marginBottom: '25px',
                textAlign: 'center',
                [theme.breakpoints.down('xs')]: {
                    display: 'block',
                    order: 1,
                },
                '& h2': {
                    fontSize: '1.875rem',
                    fontFamily: 'BebasNeue',
                    fontWeight: '400',
                    textTransform: 'uppercase',
                    [theme.breakpoints.down('xs')]: {
                        fontSize: '1.75rem',
                    },
                    [theme.breakpoints.up('sm')]: {
                        justifyContent: 'center',
                        display: 'flex',
                        flex: 'auto',
                    },
                },
            },
            '& .buttonWrapper': {
                position: 'absolute',
                right: 15,
                top: 0,
                [theme.breakpoints.down('768')]: {
                    position: 'relative',
                    order: 3,
                    width: 'calc(100% - 30px)',
                    left: 15,
                    right: 15,
                },
                '& .buttonStyle': {
                    width: '100%',
                    fontSize: '15px',
                    background: '#B9E529',
                    margin: 'auto',
                    padding: '15px 30px',
                    textTransform: 'uppercase',
                    fontWeight: '600',
                    textDecoration: 'none',
                    display: 'block',
                    border: 'none',
                    cursor: 'pointer',
                    height: '55px',
                    [theme.breakpoints.down('768')]: {
                        padding: '20px 30px',
                        textAlign: 'center',
                    },
                },
            },
        },
        '&.show-favorite-icon': {
            '& .slick-slide': {
                '&:hover svg': {
                    display: 'block',
                },
                '& svg': {
                    display: 'unset !important',
                },
            },
            '& .slick-list': {
                padding: '5px 0px 30px !important',
                [theme.breakpoints.down('sm')]: {
                    padding: '5px 0px !important',
                },
            },
            '& svg': {
                [theme.breakpoints.down('768')]: {
                    display: 'block',
                },
                [theme.breakpoints.between('768', '1025')]: {
                    display: 'block',
                },
            },
            '& .carouselWrapper': {
                minHeight: '347.5px',
                [theme.breakpoints.down('768')]: {
                    order: 2,
                    minHeight: '260px',
                },
            },
        },
    },
    headingH: {
        marginTop: 0,
        marginBottom: 0,
    },
}));

const PlantsTrendingProductsBuilder = ({
    blockData: {
        title = '',
        title_font_color = '',
        category_id_or_identifier = '',
        number_of_products_to_show = '',
    },
    brand = {},
    presentationFamily,
    forSearchPage,
}) => {
    const classes = useStyles();
    const url = category_id_or_identifier;
    const PRODUCT_QUERY = findCategoryPageByUrl(brand.domain, url, 'standard_collections', '', '', '', 'USD', (number_of_products_to_show || 8));
    const { loading, data } = useQuery(PRODUCT_QUERY);
    const dispatch = useDispatch();
    const history = useHistory();
    const isSmallScreen = !useMediaQuery('(min-width:600px)');
    if (loading) return 'Loading...';

    const products = data.findCategoryPageByUrl.category.products;
    const isSliderEnabled = isSmallScreen && products.length < 3 ? false : !isSmallScreen && products.length <= 5 ? false : true; /* eslint-disable-line */
    const isCenterModeEnabled = !isSmallScreen && products.length <= 5 ? false : isSmallScreen && products.length < 3 ? false : true; /* eslint-disable-line */
    const carousel_settings = {
        dots: false,
        infinite: isSliderEnabled,
        speed: 300,
        slidesToShow: forSearchPage ? 4 : 5,
        slidesToScroll: 1,
        centerMode: !forSearchPage && isCenterModeEnabled,
        centerPadding: '0px',
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    slidesToShow: 3,
                    slidesToScroll: 1,
                },
            },
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 3,
                    slidesToScroll: 1,
                },
            },
            {
                breakpoint: 480,
                settings: {
                    arrows: false,
                    slidesToShow: 2.2,
                    slidesToScroll: 1,
                    dots: false,
                    centerPadding: '15px',
                },
            },
        ],
    };

    const urlRewrite = (href) => {
        const regExp = /^\/c\//gi;
        return regExp.test(href) ? href : `/c${href}`;
    };

    const handleShopAllClick = () => {
        dispatch(trackEvent({
            eventCategory: 'Homepage',
            eventAction: 'Click',
            eventLabel: 'Most Loved By Our Customers - Shop All',
            eventName: 'feature_interaction',
        }));
        history.push(urlRewrite(url));
    };

    const shopAll = (small = false) => (
        <div className={`buttonWrapper ${small ? 'container' : ''}`}>
            <button onClick={handleShopAllClick} className="buttonStyle" style={{ color: title_font_color }} type="submit" aria-label="SHOP ALL">SHOP ALL</button>
        </div>
    );

    return (
        <Container id="home-page-featured-container" className="plants-featured-container">
            <Container className={`${classes.plantsFeatured} featuredPlants show-favorite-icon`} style={{ marginBottom: '0' }}>
                <div className="featuredTitle" style={{ color: title_font_color }}>
                    <h2 className={classes.headingH} aria-label={title}>{title}</h2>
                </div>
                {!forSearchPage && shopAll()}
                <div className="carouselWrapper">
                    <CustomCarousel withPrice="1" data={products ? [...products] : []} presentationFamily={presentationFamily}  carousel_settings={carousel_settings} isTopTrendingProduct brand={brand} />
                </div>
            </Container>
        </Container>
    );
};

PlantsTrendingProductsBuilder.propTypes = {
    blockData: shape({
        title: string,
        title_font_color: string,
        category_id_or_identifier: string,
        button_background_color: string,
        button_text: string,
        button_text_color: string,
        button_link: shape({
            title: string,
            href: string,
        }),
    }).isRequired,
    brand: object.isRequired,
    presentationFamily: string.isRequired,
    forSearchPage: bool,
};

PlantsTrendingProductsBuilder.defaultProps = {
    forSearchPage: false,
};
export default PlantsTrendingProductsBuilder;
